import { Component, computed, EventEmitter, input, Input, OnInit, Output, output, SimpleChanges } from '@angular/core';
import {
  PatientPlaylistModel,
  PatientProgramModel,
  PatientTargetModel,
} from '../../../core/api/models';
import { StaffApiService, SupervisorApiService } from '../../../core/api/services';
import { AlertService } from '../../../shared/services/alert.service';
import { Utils } from '../../../core/utils';
import { SupervisorHelperService } from '../../supervisor.helper.service';
import { SupervisorBaseComponent } from '../../supervisor-base.component';
import { AuthService } from '../../../auth/auth.service';
import { PatientDataFacadeService } from '../../../core/services/patient-data-facade.service';

@Component({
  selector: 'app-playlist',
  templateUrl: './playlist.component.html',
  styleUrl: '../../supervisor-main.component.css',
  standalone: false,
})
export class PlaylistComponent extends SupervisorBaseComponent {
  patientID = input.required<string>();
  @Input() playlist: PatientPlaylistModel | null = null;

  // Expansion state inputs from parent
  @Input() programExpandedStatus: { [key: number]: boolean } = {};
  @Input() targetExpandedStatus: { [key: number]: boolean } = {};

  // Order functionality inputs from parent
  @Input() orderMode: 'alphabetical' | 'custom' = 'alphabetical';
  @Input() isEditMode: boolean = false;
  
  // Selection functionality inputs from parent
  @Input() isSelecting: boolean = false;
  @Input() selectAll: boolean = false;

  // Expansion state change outputs to parent
  @Output() programExpandedChange = new EventEmitter<{programId: number, isExpanded: boolean}>();
  @Output() targetExpandedChange = new EventEmitter<{targetId: number, isExpanded: boolean}>();

  // Order functionality outputs to parent
  @Output() moveUp = new EventEmitter<number>();
  @Output() moveDown = new EventEmitter<number>();
  @Output() moveToTop = new EventEmitter<number>();
  @Output() moveToBottom = new EventEmitter<number>();
  
  // Selection functionality
  playlistSelected: boolean = false;
  @Output() selectPlaylist = new EventEmitter<number>();

  _isExpanded: boolean = false;
  @Input()
  get isExpanded(): boolean {
    return !!this._isExpanded;
  }

  set isExpanded(val: boolean) {
    this._isExpanded = !!val;
    this.isExpandedChange.emit(this._isExpanded); // Emit changes
  }

  @Output() isExpandedChange = new EventEmitter<boolean>();

  constructor(
    protected override supervisorApiService: SupervisorApiService,
    protected override alertService: AlertService,
    protected override supervisorHelperService: SupervisorHelperService,
    protected override authService: AuthService,
    protected override staffApiService: StaffApiService,
    patientDataFacadeService: PatientDataFacadeService
  ) {
    super(
      supervisorApiService,
      alertService,
      supervisorHelperService,
      authService,
      staffApiService,
      patientDataFacadeService
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isSelecting']) {
      if (this.isSelecting == false) {
        this.playlistSelected = false;
      }
    }
    if (changes['selectAll']) {
      if (this.selectAll == true) {
        this.playlistSelected = true;
        this.selectPlaylist.emit(this.playlist?.ID ?? 0)
      }
      else
        this.playlistSelected = false;
    }
  }

  programForID(programID: number) {
    return this.supervisorHelperService
      .programs()
      .find((p) => p.ID === programID);
  }

  orderedItems() {
    // Sort the items by OrderNr ascending
    return this.playlist?.Items?.sort((a, b) => (a.OrderNr ?? 0) - (b.OrderNr ?? 1));
  }

  targetForID(targetID: number) {
    return this.supervisorHelperService
      .targets()
      .find((t) => t.ID === targetID);
  }

  toggleCollapsed() {
    if (!this.isSelecting) {
      this.isExpanded = !this.isExpanded;
      const tourEvent = new Event('playlist-clicked');
      document.getElementById('playlists')?.dispatchEvent(tourEvent);
    }
    else {
      this.playlistSelected = !this.playlistSelected;
      this.selectPlaylist.emit(this.playlist?.ID ?? 0)
    }
  }

  get playlistNameDisplayName() {
    return this.playlist?.PlaylistName || 'No Name';
  }

  get playlistItemsNames() {
    const itemsNames = this.playlist?.Items?.map((item) => {
      if (item.PatientProgramID != null) {
        return this.programForID(item.PatientProgramID)?.ProgramName;
      } else if (item.PatientTargetID != null) {
        return this.targetForID(item.PatientTargetID)?.TargetName;
      } else {
        return 'Item has no name';
      }
    });
    return itemsNames?.join(', ') || 'No Items';
  }

  // Handler for program expansion changes
  onProgramExpandedChange(programId: number, isExpanded: boolean) {
    this.programExpandedChange.emit({ programId, isExpanded });
  }

  // Handler for target expansion changes
  onTargetExpandedChange(targetId: number, isExpanded: boolean) {
    this.targetExpandedChange.emit({ targetId, isExpanded });
  }

  editPlaylist() {
    console.log('editPlaylist clicked!');
    this.supervisorHelperService.additPlaylistButtonClicked(
      this.playlist ?? null
    );
  }

  restorePlaylist() {
    this.supervisorApiService
      .RestorePatientPlaylist({ patientPlaylistID: this.playlist?.ID ?? 0 })
      .subscribe(() => {
        this.alertService.success('Playlist restored');
        this.supervisorHelperService.triggerUpdateSignal();
      });
  }
  
  deletePlaylist() {
    console.log('deletePlaylist clicked!');

    this.alertService
      .confirm(
        'Delete Playlist',
        'Are you sure you want to delete this playlist? It will be marked as deleted.'
      )
      .subscribe((result) => {
        if (result) {
          this.deletePlaylistConfirmed();
        }
      });
  }

  deletePlaylistConfirmed() {
    console.log('deletePlaylistConfirmed!');
    if (this.playlist?.ID != null) {
      this.supervisorApiService
        .MarkPatientPlaylistAsDeleted({ patientPlaylistID: this.playlist.ID })
        .subscribe(
          () => {
            this.alertService.success('Playlist marked as deleted');
            this.supervisorHelperService.triggerUpdateSignal();
          },
          (error) => {
            console.error(error);
            this.alertService.error(
              Utils.getErrorMessageFromErrorResponse(error)
            );
          }
        );
    }
  }

  // Order control methods
  onMoveUp() {
    if (this.playlist?.ID) {
      this.moveUp.emit(this.playlist.ID);
    }
  }

  onMoveDown() {
    if (this.playlist?.ID) {
      this.moveDown.emit(this.playlist.ID);
    }
  }

  onMoveToTop() {
    if (this.playlist?.ID) {
      this.moveToTop.emit(this.playlist.ID);
    }
  }

  onMoveToBottom() {
    if (this.playlist?.ID) {
      this.moveToBottom.emit(this.playlist.ID);
    }
  }

  isPlaylistSelected(): boolean {
    if (this.playlistSelected === true && this.isSelecting === true)
      return true;

    return false;
  }
}
